import { Card } from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import {
  FrameAction,
  FrameActionType,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { BroadcastScheduleProgramBodySegmentToolkitNavigation } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitNavigation';
import { ESegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useSegmentLogoPermissions } from '@/features/broadcast-schedule-program/hooks/useSegmentLogoPermissions';
import { useDeleteClipLogo } from '@/features/clips/api/delete-clips-logos';
import { useClipToolkitLogoFormState } from '@/features/clips/components/ClipToolkit/components/ClipToolkitLogotipos/hooks/useClipToolkitLogoFormState';
import { TClipLogo, TPostClipLogo } from '@/features/clips/types';
import { useCachedLogoLookup } from '@/hooks/use-catalogue-entries-by-id';
import { usePresencePreviewBox } from '@/hooks/use-presence-preview-box';

import { ClipToolkitLogotiposForm } from './components/ClipToolkitLogotiposForm';
import { ClipToolkitLogotiposTable } from './components/ClipToolkitLogotiposTable';

export default function ClipToolkitLogotipos() {
  const {
    formBox,
    logoData,
    activeClip,
    setVisibleBox,
    setFormBox,
    setLogoData,
    setActiveClip,
    handleTooltikClick,
    setClipItems,
    videoControlRef,
  } = useBroadcastScheduleProgram();
  const { mutateAsync: deleteLogoClip } = useDeleteClipLogo({});
  const { t } = useTranslation();
  const findLogo = useCachedLogoLookup();
  const { handleUpdate } = useClipToolkitLogoFormState();

  const {
    canCreate: canCreateLogo,
    canUpdate: canUpdateLogo,
    canDelete: canDeleteLogo,
  } = useSegmentLogoPermissions();

  const previewPresenceBox = usePresencePreviewBox({
    referenceStartDatetime: activeClip?.start_datetime,
    setVisibleBox,
    videoControlRef,
  });

  const handlePreviewBox = (
    box: TClipLogo,
    frame: FrameActionType = FrameAction.START,
    currentFrame?: number
  ) => {
    if (logoData?.id !== box.id) {
      setLogoData(box);
    }

    previewPresenceBox(box, frame, currentFrame);
  };

  const handleEdit = useCallback(
    (item: TClipLogo) => {
      if (!canUpdateLogo) return;
      setFormBox('logo');
      setLogoData(item);
    },
    [setFormBox, canUpdateLogo]
  );

  const handleCreate = useCallback(() => {
    if (!canCreateLogo) return;
    setFormBox('logo');

    setLogoData(null);
  }, [setFormBox, setLogoData, canCreateLogo]);

  const handleDelete = useCallback(
    (item: TClipLogo, silently?: boolean) => {
      if (!canDeleteLogo) return;
      deleteLogoClip({ id: item.id || '', silently }).then(() => {
        setActiveClip(prev => {
          if (prev) {
            return {
              ...prev,
              clip_logos: prev.clip_logos?.filter(logo => logo.id !== item.id),
            };
          }
          return prev;
        });
        setClipItems(prev => {
          if (!prev) return prev;
          return prev.map(clip => {
            if (clip.id === activeClip?.id) {
              return { ...clip, clip_logos: clip.clip_logos?.filter(logo => logo.id !== item.id) };
            }
            return clip;
          });
        });
      });
    },
    [canDeleteLogo]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLocaleLowerCase() === 'i') {
        event.preventDefault();
        handleCreate();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreate]);

  const handleEditLogoInline = useCallback(
    (item: TPostClipLogo, silently?: boolean, currentFrame?: number) => {
      if (!canUpdateLogo) return;
      const editLogoClipParams: TPostClipLogo = {
        ...item,
        silently: silently || false,
      };

      handleUpdate(editLogoClipParams).then(() => {
        const logo = findLogo(item.logo_id);
        const itemLogo = {
          ...item,
          entity_label: logo?.name || '',
          start_datetime: item?.start_datetime?.toString(),
          end_datetime: item?.end_datetime?.toString(),
        } as TClipLogo;
        handlePreviewBox(itemLogo, FrameAction.CURRENT, currentFrame);
      });
    },
    [canUpdateLogo]
  );

  return (
    <Card
      classes={{
        rootSection: clsx('w-full flex-auto order-2 lg:order-3'),
        container: 'py-size-4',
      }}
    >
      <div className="flex w-full flex-col gap-2">
        {formBox !== 'logo' ? (
          <>
            <GeneralHeader
              name={t('Logotipos')}
              buttonName={canCreateLogo ? t('Añadir') : undefined}
              onClick={handleCreate}
              onClose={() => handleTooltikClick(ESegmentToolkit.LOGOTIPO)}
            />
            <div className="flex max-h-[calc(100vh-236px)] min-h-[400px] flex-col overflow-y-auto">
              <ClipToolkitLogotiposTable
                items={activeClip?.clip_logos || []}
                onPreview={handlePreviewBox}
                onUpdate={canUpdateLogo ? handleEdit : undefined}
                onDelete={canDeleteLogo ? handleDelete : undefined}
                onUpdateInline={canUpdateLogo ? handleEditLogoInline : undefined}
              />
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <ClipToolkitLogotiposForm />
            {logoData && (
              <BroadcastScheduleProgramBodySegmentToolkitNavigation
                items={activeClip?.clip_logos || []}
                activeItem={logoData}
                onChangeData={setLogoData}
              />
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
